/**
 * Meeting detail (F1): occurrences materialized in a window (RRULE +
 * per-occurrence exceptions, church TZ), GUESTS, TYPED ACTIVITIES
 * (sermon/songs/offering/prayer/announcements…) with PHOTOS, and
 * OFFLINE-FIRST ATTENDANCE (headcount + per-person check-in with a
 * deterministic ID — two offline devices converge when reconciling).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  attendanceEntryId,
  defaultWindow,
  deterministicUuid,
  getActivityPhotosQuery,
  getAttendanceEntriesQuery,
  getAttendanceSessionsQuery,
  getGroupMembersQuery,
  getMeetingActivitiesQuery,
  getMeetingExceptionsQuery,
  getMeetingInvitesQuery,
  getMeetingsQuery,
  getPeopleQuery,
  getSongsQuery,
  materializeOccurrences,
  type Occurrence,
  parseInstant,
  patchRowVerified,
  queryKeys,
  summarizeAttendance,
} from "@agape/shared";
import {
  activity_photo,
  type ActivityPhotoRecord,
  attendance_entry,
  attendance_session,
  type AttendanceEntryRecord,
  type AttendanceSessionRecord,
  eq,
  type GroupMemberRecord,
  meeting_activity,
  meeting_exception,
  meeting_invite,
  type MeetingActivityRecord,
  type MeetingExceptionRecord,
  type MeetingInviteRecord,
  type MeetingRecord,
  type PersonRecord,
  type SongRecord,
  sql,
} from "@agape/powersync";
import {
  CalendarClock,
  CalendarX2,
  Camera,
  ClipboardCheck,
  ListPlus,
  Minus,
  Plus,
  Trash2,
  UserPlus,
} from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { resolveStorageUrl } from "@/lib/api";
import { Field, FormDialog, TextArea, UploadField } from "./form";
import { meetingKindLabel } from "./meetings-page";
import { PrayerSection } from "./prayer-section";
import { ScheduleSection } from "./schedule-section";
import {
  formatWhen,
  localInputToIso,
  newId,
  nowIso,
  useDomainQuery,
  useGrants,
  useWriter,
} from "../use-domain";

const ACTIVITY_KINDS = [
  "sermon",
  "song",
  "offering",
  "prayer",
  "announcement",
  "reading",
  "other",
] as const;

export function MeetingDetailPage(props: { meetingId: string }) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const meetings = useDomainQuery<MeetingRecord>(
    (uid) => queryKeys.domain.meetings(uid),
    (db) => getMeetingsQuery(db),
  );
  const exceptions = useDomainQuery<MeetingExceptionRecord>(
    (uid) => queryKeys.domain.meetingExceptions(uid),
    (db) => getMeetingExceptionsQuery(db),
  );
  const invites = useDomainQuery<MeetingInviteRecord>(
    (uid) => queryKeys.domain.meetingInvites(uid),
    (db) => getMeetingInvitesQuery(db),
  );
  const activities = useDomainQuery<MeetingActivityRecord>(
    (uid) => queryKeys.domain.meetingActivities(uid),
    (db) => getMeetingActivitiesQuery(db),
  );
  const photos = useDomainQuery<ActivityPhotoRecord>(
    (uid) => queryKeys.domain.activityPhotos(uid),
    (db) => getActivityPhotosQuery(db),
  );
  const sessions = useDomainQuery<AttendanceSessionRecord>(
    (uid) => queryKeys.domain.attendanceSessions(uid),
    (db) => getAttendanceSessionsQuery(db),
  );
  const entries = useDomainQuery<AttendanceEntryRecord>(
    (uid) => queryKeys.domain.attendanceEntries(uid),
    (db) => getAttendanceEntriesQuery(db),
  );
  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );
  const groupMembers = useDomainQuery<GroupMemberRecord>(
    (uid) => queryKeys.domain.groupMembers(uid),
    (db) => getGroupMembersQuery(db),
  );
  const songs = useDomainQuery<SongRecord>(
    (uid) => queryKeys.domain.songs(uid),
    (db) => getSongsQuery(db),
  );

  const meeting = createMemo(() => (meetings.data ?? []).find((m) => m.id === props.meetingId));
  const scope = () => [meeting()?.group_id, meeting()?.venue_id, meeting()?.church_id];
  const canWrite = () => grants.can(scope(), "meetings", "write");
  const canAttend = () =>
    grants.can([meeting()?.group_id, meeting()?.church_id], "attendance", "write");

  const myExceptions = createMemo(() =>
    (exceptions.data ?? []).filter((e) => e.meeting_id === props.meetingId),
  );
  const occurrences = createMemo<Occurrence[]>(() => {
    const m = meeting();
    if (!m) {
      return [];
    }
    return materializeOccurrences(m, defaultWindow(Date.now(), 60, 14), myExceptions());
  });
  const myInvites = createMemo(() =>
    (invites.data ?? []).filter((i) => i.meeting_id === props.meetingId),
  );
  const myActivities = createMemo(() =>
    (activities.data ?? []).filter((a) => a.meeting_id === props.meetingId),
  );

  // ── Guests ────────────────────────────────────────────────────────────────
  const [inviteOpen, setInviteOpen] = createSignal(false);
  const [invitePerson, setInvitePerson] = createSignal("");
  // STABLE list (it does not shrink when inviting) — same reason as in group-detail.
  const invitablePeople = createMemo(() =>
    (people.data ?? []).map((p) => ({ value: p.id, label: p.name ?? p.id })),
  );
  const addInvite = async () => {
    const personId = invitePerson();
    if (!personId || myInvites().some((i) => i.person_id === personId)) {
      return;
    }
    // Reset BEFORE inserting: if the selected option disappears from the
    // mounted Select's options (the person becomes invited), Kobalte
    // crashes when re-rendering the Value.
    setInviteOpen(false);
    setInvitePerson("");
    await write().insert(meeting_invite).values({
      id: newId(),
      meeting_id: props.meetingId,
      church_id: meeting()?.church_id,
      person_id: personId,
      status: "invited",
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    showToast(t(msg`Invitation added`), "success");
  };
  const setInviteStatus = async (id: string, status: string) => {
    try {
      await patchRowVerified(write(), meeting_invite, meeting_invite.id, id, { status });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };
  const removeInvite = async (id: string) => {
    await write().delete(meeting_invite).where(eq(meeting_invite.id, id));
  };

  // ── Per-occurrence exceptions ─────────────────────────────────────────────
  const cancelOccurrence = async (occ: Occurrence) => {
    await write()
      .insert(meeting_exception)
      .values({
        id: newId(),
        meeting_id: props.meetingId,
        church_id: meeting()?.church_id,
        occurrence_start: new Date(occ.originalStart).toISOString(),
        kind: "cancelled",
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    showToast(t(msg`Occurrence cancelled`), "success");
  };
  const [moveTarget, setMoveTarget] = createSignal<Occurrence | null>(null);
  const [moveTo, setMoveTo] = createSignal("");
  const moveOccurrence = async () => {
    const occ = moveTarget();
    const newStart = localInputToIso(moveTo());
    if (!occ || !newStart) {
      return;
    }
    await write()
      .insert(meeting_exception)
      .values({
        id: newId(),
        meeting_id: props.meetingId,
        church_id: meeting()?.church_id,
        occurrence_start: new Date(occ.originalStart).toISOString(),
        kind: "moved",
        new_start: newStart,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setMoveTarget(null);
    setMoveTo("");
    showToast(t(msg`Occurrence moved`), "success");
  };
  const restoreOccurrence = async (occ: Occurrence) => {
    if (!occ.exceptionId) {
      return;
    }
    await write().delete(meeting_exception).where(eq(meeting_exception.id, occ.exceptionId));
  };

  // ── Typed activities + photos ─────────────────────────────────────────────
  const [activityOpen, setActivityOpen] = createSignal(false);
  const [activityOccurrence, setActivityOccurrence] = createSignal<number | null>(null);
  const [activityKind, setActivityKind] = createSignal<string>("sermon");
  const [activityTitle, setActivityTitle] = createSignal("");
  const [activityContent, setActivityContent] = createSignal("");
  const [activitySong, setActivitySong] = createSignal("");
  const activityKindLabel = (kind: string | null) => {
    switch (kind) {
      case "sermon": {
        return t(msg`Sermon / message`);
      }
      case "song": {
        return t(msg`Song`);
      }
      case "offering": {
        return t(msg`Offering`);
      }
      case "prayer": {
        return t(msg`Prayer`);
      }
      case "announcement": {
        return t(msg`Announcements`);
      }
      case "reading": {
        return t(msg`Reading`);
      }
      default: {
        return t(msg`Other`);
      }
    }
  };
  const openActivity = (occurrenceStart: number | null) => {
    setActivityOccurrence(occurrenceStart);
    setActivityKind("sermon");
    setActivityTitle("");
    setActivityContent("");
    setActivitySong("");
    setActivityOpen(true);
  };
  const addActivity = async () => {
    if (!activityTitle().trim()) {
      return;
    }
    const occ = activityOccurrence();
    await write()
      .insert(meeting_activity)
      .values({
        id: newId(),
        meeting_id: props.meetingId,
        church_id: meeting()?.church_id,
        occurrence_start: occ === null ? null : new Date(occ).toISOString(),
        kind: activityKind(),
        title: activityTitle().trim(),
        content: activityContent() || null,
        song_id: activityKind() === "song" && activitySong() ? activitySong() : null,
        sort_order: myActivities().length + 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setActivityOpen(false);
    showToast(t(msg`Activity added`), "success");
  };
  const trashActivity = async (id: string) => {
    try {
      await patchRowVerified(write(), meeting_activity, meeting_activity.id, id, {
        deleted_at: nowIso(),
      });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };
  const [photoActivity, setPhotoActivity] = createSignal<string | null>(null);
  const [photoUrl, setPhotoUrl] = createSignal("");
  const [photoCaption, setPhotoCaption] = createSignal("");
  const photosOf = (activityId: string) =>
    (photos.data ?? []).filter((p) => p.activity_id === activityId);
  const addPhoto = async () => {
    const activityId = photoActivity();
    if (!activityId || !photoUrl().trim()) {
      return;
    }
    await write()
      .insert(activity_photo)
      .values({
        id: newId(),
        activity_id: activityId,
        church_id: meeting()?.church_id,
        url: photoUrl().trim(),
        caption: photoCaption().trim() || null,
        sort_order: photosOf(activityId).length,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setPhotoActivity(null);
    setPhotoUrl("");
    setPhotoCaption("");
    showToast(t(msg`Photo added`), "success");
  };
  const removePhoto = async (id: string) => {
    await write().delete(activity_photo).where(eq(activity_photo.id, id));
  };

  // ── Offline-first attendance ──────────────────────────────────────────────
  // Session with a DETERMINISTIC ID per occurrence: two offline devices open
  // the SAME session and their check-ins converge (docs/OFFLINE_ATTENDANCE.md).
  const sessionIdFor = (occ: Occurrence) =>
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- internal key, not UI
    deterministicUuid(`attendance_session:${props.meetingId}:${occ.originalStart}`);
  const sessionFor = (occ: Occurrence) =>
    (sessions.data ?? []).find(
      (s) =>
        s.id === sessionIdFor(occ) ||
        (s.meeting_id === props.meetingId &&
          parseInstant(s.occurrence_start) === occ.originalStart),
    );
  const entriesFor = (sessionId: string) =>
    (entries.data ?? []).filter((e) => e.session_id === sessionId);

  const [attendanceOcc, setAttendanceOcc] = createSignal<Occurrence | null>(null);
  const openAttendance = async (occ: Occurrence) => {
    if (!sessionFor(occ) && canAttend()) {
      await write()
        .insert(attendance_session)
        .values({
          id: sessionIdFor(occ),
          meeting_id: props.meetingId,
          church_id: meeting()?.church_id,
          occurrence_start: new Date(occ.originalStart).toISOString(),
          headcount: null,
          created_at: nowIso(),
          updated_at: nowIso(),
        });
    }
    setAttendanceOcc(occ);
  };
  const bumpHeadcount = async (session: AttendanceSessionRecord, delta: number) => {
    // ATOMIC increment in SQL: rapid taps cannot lose counts by reading a
    // stale value from the reactive query (read-modify-write).
    // `patchRowVerified` cannot compare this expression by value (it is not a
    // literal) — `verifyBy: "updated_at"` re-reads and verifies that THIS
    // attempt matched a row (see piece 3 of the note in verified-write.ts) and
    // retries if the attendance session is not in the view yet.
    try {
      await patchRowVerified(
        write(),
        attendance_session,
        attendance_session.id,
        session.id,
        { headcount: sql`MAX(0, COALESCE(headcount, 0) + ${delta})` },
        { verifyBy: "updated_at" },
      );
    } catch {
      showToast(t(msg`Couldn't save the headcount — try again`), "error");
    }
  };
  const toggleCheckIn = async (session: AttendanceSessionRecord, person: { id: string }) => {
    const entryId = attendanceEntryId(session.id, person.id);
    const existing = (entries.data ?? []).find((e) => e.id === entryId);
    if (existing) {
      await write().delete(attendance_entry).where(eq(attendance_entry.id, entryId));
    } else {
      await write().insert(attendance_entry).values({
        id: entryId,
        session_id: session.id,
        church_id: session.church_id,
        person_id: person.id,
        status: "present",
        checked_in_at: nowIso(),
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    }
  };
  /** Roster: group (cell) members + guests + the rest of the directory. */
  const roster = createMemo(() => {
    const m = meeting();
    const seen = new Set<string>();
    const rows: { id: string; name: string }[] = [];
    const push = (id: string | null, name: string | null) => {
      if (id && !seen.has(id)) {
        seen.add(id);
        rows.push({ id, name: name ?? id });
      }
    };
    if (m?.group_id) {
      for (const gm of groupMembers.data ?? []) {
        if (gm.group_id === m.group_id) {
          push(gm.person_id, gm.person_name);
        }
      }
    }
    for (const invite of myInvites()) {
      push(invite.person_id, invite.person_name);
    }
    for (const person of (people.data ?? []).filter((row) => row.church_id === m?.church_id)) {
      push(person.id, person.name);
    }
    return rows;
  });

  const occurrenceActivities = (occ: Occurrence | null) =>
    myActivities().filter((a) => {
      const at = parseInstant(a.occurrence_start);
      return occ === null ? at === null : at === occ.originalStart || at === null;
    });

  return (
    <div class="space-y-6">
      <PageSubHeader title={meeting()?.title ?? t(msg`Meeting`)} backTo="/meetings" />
      <Show
        when={meeting()}
        fallback={
          <p class="text-muted-foreground">{t(msg`This meeting is not synced to your account.`)}</p>
        }
      >
        {(m) => (
          <>
            <Card data-testid="meeting-info">
              <CardContent class="space-y-1 py-4">
                <p class="text-sm text-muted-foreground">
                  {[
                    meetingKindLabel(t, m().kind),
                    m().is_public ? t(msg`Public`) : t(msg`Private`),
                    (() => {
                      const organizer = m().organizer_name;
                      return organizer ? t(msg`Organizer: ${organizer}`) : null;
                    })(),
                    m().location_detail,
                    m().timezone,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <Show when={m().description}>
                  <p class="text-sm text-muted-foreground">{m().description}</p>
                </Show>
                <Show when={m().rrule}>
                  <p class="font-mono text-xs text-muted-foreground" data-testid="meeting-rrule">
                    {m().rrule}
                  </p>
                </Show>
              </CardContent>
            </Card>

            {/* ── Invitados ─────────────────────────────────────────────── */}
            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Guests`)}
                </h3>
                <Show when={canWrite()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="invite-add"
                    onClick={() => setInviteOpen(true)}
                  >
                    <UserPlus size={14} /> {t(msg`Invite`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={myInvites().length > 0}
                fallback={<p class="text-sm text-muted-foreground">{t(msg`No guests yet.`)}</p>}
              >
                <For each={myInvites()}>
                  {(invite) => (
                    <Card data-testid="invite-item">
                      <CardContent class="flex items-center justify-between gap-3 py-2.5">
                        <p class="min-w-0 truncate text-sm font-medium">{invite.person_name}</p>
                        <div class="flex shrink-0 items-center gap-2">
                          <Show
                            when={canWrite()}
                            fallback={
                              <span class="text-xs text-muted-foreground">{invite.status}</span>
                            }
                          >
                            <div class="w-32">
                              <Select
                                options={[
                                  { value: "invited", label: t(msg`Invited`) },
                                  {
                                    value: "confirmed",
                                    label: t(msg`Confirmed`),
                                  },
                                  {
                                    value: "declined",
                                    label: t(msg`Declined`),
                                  },
                                ]}
                                value={invite.status ?? "invited"}
                                onChange={(v) => void setInviteStatus(invite.id, v)}
                                aria-label={t(msg`Invitation status`)}
                              />
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t(msg`Remove guest`)}
                              onClick={() => void removeInvite(invite.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </Show>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </Show>
            </section>

            {/* ── Cronograma (bloques ordenados — F2) ───────────────────── */}
            <ScheduleSection meetingId={props.meetingId} meeting={meeting} canWrite={canWrite} />

            {/* ── Actividades de la serie (todas las ocurrencias) ───────── */}
            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Series activities`)}
                </h3>
                <Show when={canWrite()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="activity-add"
                    onClick={() => openActivity(null)}
                  >
                    <ListPlus size={14} /> {t(msg`Add activity`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={occurrenceActivities(null).length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground" data-testid="activities-empty">
                    {t(msg`No activities yet.`)}
                  </p>
                }
              >
                <For each={occurrenceActivities(null)}>
                  {(a) => (
                    <Card data-testid="activity-item" data-activity-kind={a.kind}>
                      <CardContent class="space-y-2 py-2.5">
                        <div class="flex items-center justify-between gap-3">
                          <div class="min-w-0">
                            <p class="truncate text-sm font-medium">{a.title}</p>
                            <p class="truncate text-xs text-muted-foreground">
                              {[
                                activityKindLabel(a.kind),
                                a.occurrence_start
                                  ? formatWhen(parseInstant(a.occurrence_start) ?? 0)
                                  : t(msg`Every occurrence`),
                              ].join(" · ")}
                            </p>
                            <Show when={a.content}>
                              <p class="truncate text-xs text-muted-foreground">{a.content}</p>
                            </Show>
                          </div>
                          <Show when={canWrite()}>
                            <div class="flex shrink-0 gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Add photo`)}
                                data-testid="activity-photo-add"
                                onClick={() => setPhotoActivity(a.id)}
                              >
                                <Camera size={14} />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Move activity to trash`)}
                                onClick={() => void trashActivity(a.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </Show>
                        </div>
                        {/* Peticiones de oración (F2): en actividades de oración */}
                        <Show when={a.kind === "prayer"}>
                          <PrayerSection activity={() => a} />
                        </Show>
                        <Show when={photosOf(a.id).length > 0}>
                          <div class="flex flex-wrap gap-2" data-testid="activity-gallery">
                            <For each={photosOf(a.id)}>
                              {(photo) => (
                                <figure class="group relative">
                                  <img
                                    src={resolveStorageUrl(photo.url)}
                                    alt={photo.caption ?? a.title ?? ""}
                                    class="h-20 w-28 rounded-md border border-border object-cover"
                                    loading="lazy"
                                    decoding="async"
                                    data-testid="activity-gallery-photo"
                                  />
                                  <Show when={canWrite()}>
                                    <button
                                      type="button"
                                      class="absolute right-1 top-1 hidden rounded-full bg-background/80 p-1 group-hover:block"
                                      aria-label={t(msg`Remove photo`)}
                                      onClick={() => void removePhoto(photo.id)}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </Show>
                                </figure>
                              )}
                            </For>
                          </div>
                        </Show>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </Show>
            </section>

            {/* ── Ocurrencias en ventana ────────────────────────────────── */}
            <section class="space-y-2">
              <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t(msg`Upcoming occurrences`)}
              </h3>
              <Show
                when={occurrences().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground" data-testid="occurrences-empty">
                    {t(msg`No occurrences in the window.`)}
                  </p>
                }
              >
                <div class="space-y-2" data-testid="occurrences-list">
                  <For each={occurrences()}>
                    {(occ) => {
                      const session = () => sessionFor(occ);
                      const summary = () => {
                        const s = session();
                        return s ? summarizeAttendance(entriesFor(s.id), s.headcount) : null;
                      };
                      return (
                        <Card
                          data-testid="occurrence-item"
                          data-occurrence-status={occ.status}
                          data-occurrence-start={occ.originalStart}
                        >
                          <CardContent class="flex flex-wrap items-center justify-between gap-3 py-2.5">
                            <div class="min-w-0">
                              <p
                                class={`text-sm font-medium ${occ.status === "cancelled" ? "line-through opacity-60" : ""}`}
                              >
                                {formatWhen(occ.start)}
                              </p>
                              <p class="text-xs text-muted-foreground">
                                {(() => {
                                  if (occ.status === "cancelled") {
                                    return t(msg`Cancelled`);
                                  }
                                  if (occ.status === "moved") {
                                    const movedFrom = formatWhen(occ.originalStart);
                                    return t(msg`Moved from ${movedFrom}`);
                                  }
                                  return t(msg`Scheduled`);
                                })()}
                                <Show when={summary()}>
                                  {" "}
                                  ·{" "}
                                  {(() => {
                                    const total = summary()?.effectiveTotal ?? 0;
                                    return t(msg`Attendance: ${total}`);
                                  })()}
                                </Show>
                              </p>
                            </div>
                            <div class="flex shrink-0 flex-wrap gap-1">
                              <Show when={canAttend() && occ.status !== "cancelled"}>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  data-testid="occurrence-attendance"
                                  onClick={() => void openAttendance(occ)}
                                >
                                  <ClipboardCheck size={14} /> {t(msg`Attendance`)}
                                </Button>
                              </Show>
                              <Show when={canWrite()}>
                                <Show when={occ.status === "scheduled"}>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="occurrence-move"
                                    onClick={() => {
                                      setMoveTarget(occ);
                                      setMoveTo("");
                                    }}
                                  >
                                    {t(msg`Move`)}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="occurrence-cancel"
                                    onClick={() => void cancelOccurrence(occ)}
                                  >
                                    <CalendarX2 size={14} /> {t(msg`Cancel`)}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="occurrence-activity"
                                    onClick={() => openActivity(occ.originalStart)}
                                  >
                                    <ListPlus size={14} />
                                  </Button>
                                </Show>
                                <Show when={occ.status !== "scheduled"}>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="occurrence-restore"
                                    onClick={() => void restoreOccurrence(occ)}
                                  >
                                    {t(msg`Restore`)}
                                  </Button>
                                </Show>
                              </Show>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }}
                  </For>
                </div>
              </Show>
            </section>
          </>
        )}
      </Show>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={inviteOpen()}
        onOpenChange={setInviteOpen}
        title={t(msg`Invite to the meeting`)}
        icon={<UserPlus size={20} />}
        submitTestId="invite-submit"
        disabled={!invitePerson()}
        onSubmit={addInvite}
      >
        <Field id="inv-person" label={t(msg`Person`)}>
          <Select
            options={invitablePeople()}
            value={invitePerson()}
            onChange={setInvitePerson}
            aria-label={t(msg`Person`)}
            emptyAction={{
              label: t(msg`Add people to the directory first`),
              href: "/people",
            }}
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={moveTarget() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMoveTarget(null);
          }
        }}
        title={t(msg`Move occurrence`)}
        icon={<CalendarClock size={20} />}
        submitTestId="occurrence-move-submit"
        disabled={!moveTo()}
        onSubmit={moveOccurrence}
      >
        <p class="text-sm text-muted-foreground">
          <Show when={moveTarget()}>{(occ) => formatWhen(occ().originalStart)}</Show>
        </p>
        <Field
          id="mv-to"
          label={t(msg`New date and time`)}
          type="datetime-local"
          value={moveTo()}
          onInput={setMoveTo}
          testId="occurrence-move-input"
        />
      </FormDialog>

      <FormDialog
        open={activityOpen()}
        onOpenChange={setActivityOpen}
        title={t(msg`Add activity`)}
        icon={<ListPlus size={20} />}
        submitTestId="activity-submit"
        disabled={!activityTitle().trim()}
        onSubmit={addActivity}
      >
        <Show when={activityOccurrence() !== null}>
          <p class="text-xs text-muted-foreground">
            {(() => {
              const when = formatWhen(activityOccurrence() ?? 0);
              return t(msg`For the occurrence of ${when}`);
            })()}
          </p>
        </Show>
        <Field id="act-kind" label={t(msg`Type`)}>
          <Select
            options={ACTIVITY_KINDS.map((k) => ({
              value: k,
              label: activityKindLabel(k),
            }))}
            value={activityKind()}
            onChange={setActivityKind}
            aria-label={t(msg`Type`)}
          />
        </Field>
        <Field
          id="act-title"
          label={t(msg`Title`)}
          value={activityTitle()}
          onInput={setActivityTitle}
          testId="activity-title-input"
        />
        <Show when={activityKind() === "song"}>
          <Field id="act-song" label={t(msg`Song from the library`)}>
            <Select
              options={(songs.data ?? []).map((s) => ({
                value: s.id,
                label: s.title ?? s.id,
              }))}
              value={activitySong()}
              onChange={setActivitySong}
              aria-label={t(msg`Song from the library`)}
              emptyLabel={t(msg`No songs synced`)}
            />
          </Field>
        </Show>
        <Field id="act-content" label={t(msg`Content / notes`)}>
          <TextArea
            id="act-content"
            rows={3}
            value={activityContent()}
            onInput={setActivityContent}
            class="font-sans"
            testId="activity-content-input"
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={photoActivity() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPhotoActivity(null);
          }
        }}
        title={t(msg`Add photo`)}
        icon={<Camera size={20} />}
        submitTestId="activity-photo-submit"
        disabled={!photoUrl().trim()}
        onSubmit={addPhoto}
      >
        {/* Storage REAL (F3): el archivo sube a la plataforma y la fila
            local-first guarda la URL resultante. El campo URL queda como
            alternativa para enlazar una foto ya alojada. */}
        <UploadField
          id="ph-file"
          label={t(msg`Upload photo`)}
          testId="activity-photo-file"
          onUploaded={setPhotoUrl}
        />
        <Field
          id="ph-url"
          label={t(msg`…or photo URL`)}
          value={photoUrl()}
          onInput={setPhotoUrl}
          testId="activity-photo-url"
        />
        <Field
          id="ph-caption"
          label={t(msg`Caption`)}
          value={photoCaption()}
          onInput={setPhotoCaption}
        />
        <Show when={photoUrl().trim()}>
          <img
            src={resolveStorageUrl(photoUrl())}
            alt=""
            decoding="async"
            class="max-h-40 w-full rounded-md border border-border object-cover"
            data-testid="activity-photo-preview"
          />
        </Show>
      </FormDialog>

      {/* ── Panel de asistencia (offline-first) ──────────────────────────── */}
      <FormDialog
        open={attendanceOcc() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAttendanceOcc(null);
          }
        }}
        title={t(msg`Attendance`)}
        icon={<ClipboardCheck size={20} />}
        submitLabel={t(msg`Done`)}
        submitTestId="attendance-done"
        onSubmit={() => {
          setAttendanceOcc(null);
        }}
      >
        <Show when={attendanceOcc()}>
          {(occ) => {
            const session = () => sessionFor(occ());
            return (
              <Show
                when={session()}
                fallback={<p class="text-sm text-muted-foreground">{t(msg`Opening session…`)}</p>}
              >
                {(s) => {
                  const summary = () => summarizeAttendance(entriesFor(s().id), s().headcount);
                  return (
                    <div class="space-y-4" data-testid="attendance-panel">
                      <p class="text-sm text-muted-foreground">{formatWhen(occ().start)}</p>
                      {/* Conteo general */}
                      <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
                        <span class="text-sm font-medium">{t(msg`Headcount`)}</span>
                        <div class="flex items-center gap-3">
                          <Button
                            size="icon"
                            variant="secondary"
                            aria-label={t(msg`Decrease headcount`)}
                            data-testid="headcount-dec"
                            onClick={() => void bumpHeadcount(s(), -1)}
                          >
                            <Minus size={14} />
                          </Button>
                          <span
                            class="min-w-8 text-center font-mono text-lg"
                            data-testid="headcount-value"
                          >
                            {s().headcount ?? 0}
                          </span>
                          <Button
                            size="icon"
                            variant="secondary"
                            aria-label={t(msg`Increase headcount`)}
                            data-testid="headcount-inc"
                            onClick={() => void bumpHeadcount(s(), 1)}
                          >
                            <Plus size={14} />
                          </Button>
                        </div>
                      </div>
                      {/* Check-in nominal */}
                      <div class="space-y-1.5">
                        <p class="text-xs font-bold uppercase tracking-wider opacity-60">
                          {t(msg`Check-in by person`)}
                        </p>
                        <Show
                          when={roster().length > 0}
                          fallback={
                            <p class="text-sm text-muted-foreground">
                              {t(msg`No people available for check-in.`)}
                            </p>
                          }
                        >
                          <div
                            class="max-h-56 space-y-1 overflow-y-auto"
                            data-testid="attendance-roster"
                          >
                            <For each={roster()}>
                              {(person) => {
                                const checked = () =>
                                  (entries.data ?? []).some(
                                    (e) => e.id === attendanceEntryId(s().id, person.id),
                                  );
                                return (
                                  <label
                                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/10"
                                    data-testid="attendance-person"
                                    data-person-id={person.id}
                                    data-checked={checked() ? "true" : "false"}
                                  >
                                    <Checkbox
                                      checked={checked()}
                                      onChange={() => void toggleCheckIn(s(), person)}
                                      aria-label={person.name}
                                    />
                                    <span class="min-w-0 truncate">{person.name}</span>
                                  </label>
                                );
                              }}
                            </For>
                          </div>
                        </Show>
                      </div>
                      <p class="text-sm" data-testid="attendance-summary">
                        {(() => {
                          const { present, effectiveTotal } = summary();
                          return t(msg`Present: ${present} · Total: ${effectiveTotal}`);
                        })()}
                      </p>
                      <p class="text-xs text-muted-foreground">
                        {t(
                          msg`Works offline — check-ins queue locally and reconcile when you reconnect.`,
                        )}
                      </p>
                    </div>
                  );
                }}
              </Show>
            );
          }}
        </Show>
      </FormDialog>
    </div>
  );
}
